import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { User } from "../repository/auth.default";
import { decodeJwt, JWTPayload } from "jose";
import _ from "lodash";

export interface Auth {
  readonly token: string;
  readonly refreshToken: string;
}

export interface Payload extends JWTPayload {
  readonly permissions: string;
}

export interface AuthorizeState {
  readonly auth?: Auth | null;
  readonly user?:
    | (User & {
        readonly avatarSrc?: string;
      })
    | null;
}

const initialState: AuthorizeState = {};

export const authorizeSlice = createSlice({
  name: "authorize",
  initialState,
  reducers: {
    signIn: (state, action: PayloadAction<Auth>) => {
      state.auth = action.payload;
    },
    signOut: (state) => {
      state.auth = null;
    },
    setUser: (
      state,
      action: PayloadAction<
        | (User & {
            readonly avatarSrc?: string;
          })
        | null
      >,
    ) => {
      // Preserve an already-loaded avatar so a profile (re)load can't clobber it.
      state.user = action.payload
        ? {
            ...action.payload,
            avatarSrc: action.payload.avatarSrc ?? state.user?.avatarSrc,
          }
        : null;
    },
    // Partial merge so the avatar fetch is order-independent vs the profile fetch.
    setAvatar: (state, action: PayloadAction<string | undefined>) => {
      state.user = { ...(state.user as any), avatarSrc: action.payload };
    },
  },
});

const authSelector = (state: RootState) => state.authorize.auth;
const userSelector = (state: RootState) => state.authorize.user;
// Memoized on the token so it returns a stable reference between renders (an unmemoized selector
// returning a fresh object each call makes every consumer re-render on every store dispatch).
const userPayloadSelector = createSelector(
  (state: RootState) => state.authorize.auth?.token,
  (
    token: string | undefined,
  ):
    | (Payload & {
        readonly role?: string;
      })
    | null => {
    if (!token) {
      return null;
    }

    let payload = decodeJwt(token) as Payload;
    payload = {
      ...payload,
      role:
        Object.entries(payload).find(([key, value]) => /role$/.test(key))?.[1] ??
        null,
    };

    return payload;
  },
);
// Memoized on the auth token so the returned { payload, can } keeps a stable reference between renders
// (otherwise React-Redux warns about a selector returning a new result for the same state → extra rerenders).
const permissionsSelector = createSelector(
  (state: RootState) => state.authorize.auth?.token,
  (
    token: string | undefined,
  ): {
    readonly payload: Payload | null;
    readonly can: (
      key: string,
      action: "none" | "view" | "create" | "edit" | "delete",
    ) => boolean;
  } => {
    if (!token) {
      return {
        payload: null,
        can: () => false,
      };
    }

    let payload = decodeJwt(token) as Payload;

    const permissions: string[][] = [];

  try {
    permissions.push(
      ...(JSON.parse(payload.permissions) as string[]).map((permission) =>
        permission.split(":"),
      ),
    );

    return {
      payload,
      can: (
        key: string,
        action: "none" | "view" | "create" | "edit" | "delete",
      ) => {
        const permission = permissions.find(
          (item) => item[0].toLowerCase() == key.toLowerCase(),
        );

        if (!permission) {
          return false;
        }

        const value = permission[1];

        switch (value.toLowerCase()) {
          case "view":
            return ["view"].includes(action.toLowerCase());
          case "create":
            return ["view", "create", "edit"].includes(action.toLowerCase());
          case "edit":
            return ["view", "create", "edit"].includes(action.toLowerCase());
          case "delete":
            return ["view", "create", "edit", "delete"].includes(
              action.toLowerCase(),
            );

          default:
            return false;
        }
      },
    };
    } catch {
      return {
        payload: null,
        can: () => false,
      };
    }
  },
);

// Role selectors — composed off the memoized payload selector so consumers stop
// hand-rolling `payload?.role === "..."` comparisons (primitive result = stable reference).
const isAppAdminSelector = createSelector(
  userPayloadSelector,
  (payload) => payload?.role === "AppAdmin",
);
const isAffiliateSelector = createSelector(
  userPayloadSelector,
  (payload) =>
    payload?.role === "AppConsultant" || payload?.role === "AppCollaborator",
);

// Selectors
export const authorizeSelectors = {
  auth: authSelector,
  user: userSelector,
  permissions: permissionsSelector,
  payload: userPayloadSelector,
  isAppAdmin: isAppAdminSelector,
  isAffiliate: isAffiliateSelector,
};

// Actions
export const authorizeActions = authorizeSlice.actions;
